<template>
  <section class="order-map-card">
    <div class="order-map-head">
      <div>
        <span>ROBOT GPS LIVE MAP</span>
        <strong>机器人GPS实时位置</strong>
      </div>
      <div class="order-map-actions">
        <button type="button" @click="focusRoute">定位车辆</button>
        <button
          type="button"
          class="test-run-btn"
          :disabled="demoRouteLoading || !canStartDemo"
          @click="startDemoRoute"
        >
          {{ demoButtonText }}
        </button>
      </div>
    </div>

    <div class="order-map-layout">
      <div class="order-map-copy">
        <div class="eta-card primary">
          <span>{{ distanceLabel }}</span>
          <strong>{{ remainingDistanceText }}</strong>
          <p>{{ routeHint }}</p>
        </div>
        <div class="eta-grid">
          <div>
            <span>预计到达</span>
            <strong>{{ remainingEtaText }}</strong>
          </div>
          <div>
            <span>GPS坐标</span>
            <strong>{{ robotCoordinateText }}</strong>
          </div>
          <div>
            <span>定位来源</span>
            <strong>{{ locationSourceText }}</strong>
          </div>
          <div>
            <span>目的地</span>
            <strong>{{ destinationLabel }}</strong>
          </div>
          <div>
            <span>状态</span>
            <strong>{{ vehicleStatusText }}</strong>
          </div>
          <div>
            <span>速度/方向</span>
            <strong>{{ speedHeadingText }}</strong>
          </div>
        </div>
      </div>

      <div class="order-map-panel">
        <div
          ref="mapContainer"
          :class="['wgs-map-canvas', { 'is-panning': isPanning }]"
          @wheel.prevent="handleWheel"
          @pointerdown="startPan"
          @pointermove="movePan"
          @pointerup="endPan"
          @pointercancel="endPan"
          @lostpointercapture="endPan"
          @dblclick.prevent="handleDoubleClick"
          @dragstart.prevent
        >
          <div
            v-for="tile in visibleTiles"
            :key="tile.key"
            class="map-tile"
            :style="tile.style"
          >
            <img :src="tile.url" alt="" draggable="false" @error="handleTileError(tile, $event)" @load="handleTileLoad" />
          </div>

          <svg class="route-layer" :viewBox="`0 0 ${viewport.width} ${viewport.height}`" preserveAspectRatio="none">
            <polyline
              v-if="routeScreenPoints.length > 1"
              :points="routePolyline"
              class="route-line route-line-shadow"
            />
            <polyline
              v-if="routeScreenPoints.length > 1"
              :points="routePolyline"
              class="route-line"
            />
          </svg>

          <div
            v-if="isShuttleService"
            class="endpoint-marker pickup-marker"
            :style="pickupMarkerStyle"
          >
            <b>起</b>
            <span>上车点</span>
          </div>
          <div
            v-if="isShuttleService"
            class="endpoint-marker destination-marker"
            :style="destinationMarkerStyle"
          >
            <b>终</b>
            <span>下车点</span>
          </div>
          <div v-else class="target-marker" :style="targetMarkerStyle"></div>
          <div class="robot-marker-wrap" :style="robotMarkerStyle">
            <div class="order-robot-marker" :style="{ '--vehicle-heading': `${robotHeading}deg` }">
              <div class="vehicle-shadow"></div>
              <div class="vehicle-body">
                <div class="vehicle-roof"></div>
                <div class="vehicle-glass"></div>
                <div class="vehicle-door-line"></div>
                <span class="tail-light"></span>
                <span class="front-light"></span>
                <i class="wheel wheel-left"></i>
                <i class="wheel wheel-right"></i>
              </div>
            </div>
          </div>

          <div class="map-attribution">WGS84 / OpenStreetMap</div>
          <div v-if="demoMode" class="demo-route-badge" :class="{ running: demoRunning }">
            {{ demoStatusText }}
          </div>
          <div v-if="demoPhase === 'WAITING_PICKUP'" class="demo-pickup-confirm">
            <strong>已到达接驳起点</strong>
            <span>模拟等待乘客上车，确认后车辆继续前往终点。</span>
            <button type="button" @click.stop="continueDemoToDestination">OK，前往终点</button>
          </div>
          <div v-if="tileErrorCount > 8" class="tile-warning">
            地图瓦片加载不稳定，当前已自动切换备用源
          </div>
          <div v-if="routeDeviationAlert" class="route-deviation-warning">
            {{ routeDeviationAlert }}
          </div>
        </div>

        <div class="map-zoom-controls">
          <button type="button" @click="zoomIn">+</button>
          <button type="button" @click="zoomOut">-</button>
        </div>

        <div class="map-legend">
          <span><i class="robot-dot"></i>GPS车辆</span>
          <span v-if="isShuttleService"><i class="pickup-dot"></i>上车点</span>
          <span v-if="isShuttleService"><i class="target-dot"></i>下车点</span>
          <span v-else><i class="target-dot"></i>服务点</span>
          <span><i class="route-dot"></i>道路轨迹</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const GUIZHOU_MUSEUM_WGS84 = { lng: 106.6412, lat: 26.6480 }
const TILE_SIZE = 256
const DEFAULT_VIEWPORT = { width: 720, height: 360 }
const DEMO_ROUTE_DURATION = 28000
const TILE_PROVIDERS = [
  {
    name: 'Carto',
    url: (z, x, y) => `https://basemaps.cartocdn.com/rastertiles/voyager/${z}/${x}/${y}.png`
  },
  {
    name: 'OpenStreetMap',
    url: (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`
  },
  {
    name: 'ArcGIS',
    url: (z, x, y) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/${z}/${y}/${x}`
  }
]

export default {
  name: 'OrderVehicleMap',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      viewport: { ...DEFAULT_VIEWPORT },
      zoom: 14,
      center: { ...GUIZHOU_MUSEUM_WGS84 },
      resizeObserver: null,
      isPanning: false,
      activePointerId: null,
      lastPanPoint: null,
      pendingPanDelta: { x: 0, y: 0 },
      panRaf: null,
      wheelDeltaY: 0,
      wheelAnchor: null,
      wheelRaf: null,
      userAdjustedMap: false,
      tileProviderIndex: 0,
      tileErrorCount: 0,
      demoMode: false,
      demoRunning: false,
      demoRouteLoading: false,
      demoRouteError: '',
      demoRoutePoints: [],
      demoRouteDistanceMeters: null,
      demoRouteDurationSeconds: null,
      demoRobotPosition: null,
      demoRobotHeading: null,
      demoPhase: 'IDLE',
      demoStartTs: 0,
      demoAnimationFrame: null,
      plannedRouteLoading: false,
      plannedRouteError: '',
      plannedRoutePoints: [],
      plannedRouteDistanceMeters: null,
      plannedRouteDurationSeconds: null,
      plannedRouteKey: ''
    }
  },
  computed: {
    isShuttleService() {
      return this.order.serviceType === 'SHUTTLE'
    },
    canStartDemo() {
      if (!this.isValidLngLat(this.robotPosition.lng, this.robotPosition.lat)) return false
      if (this.isShuttleService) {
        return this.isValidLngLat(this.pickupPosition.lng, this.pickupPosition.lat) &&
          this.isValidLngLat(this.destinationPosition.lng, this.destinationPosition.lat) &&
          (
            this.calculateDistanceMeters(this.robotPosition, this.pickupPosition) > 5 ||
            this.calculateDistanceMeters(this.pickupPosition, this.destinationPosition) > 5
          )
      }
      return this.isValidLngLat(this.destinationPosition.lng, this.destinationPosition.lat) &&
        this.calculateDistanceMeters(this.robotPosition, this.destinationPosition) > 5
    },
    displayedRobotPosition() {
      return this.demoRobotPosition || this.robotPosition
    },
    displayedRoutePoints() {
      if (this.demoRoutePoints.length > 1) {
        return this.demoRoutePoints
      }
      if (this.plannedRoutePoints.length > 1) {
        return this.plannedRoutePoints
      }
      return [this.displayedRobotPosition, this.routeTargetPosition].filter(Boolean)
    },
    hasRealRobotPosition() {
      const lng = this.numberValue(this.order.robotLng ?? this.order.vehicleLng ?? this.order.currentLng)
      const lat = this.numberValue(this.order.robotLat ?? this.order.vehicleLat ?? this.order.currentLat)
      return this.isValidLngLat(lng, lat)
    },
    robotPosition() {
      const lng = this.numberValue(this.order.robotLng ?? this.order.vehicleLng ?? this.order.currentLng)
      const lat = this.numberValue(this.order.robotLat ?? this.order.vehicleLat ?? this.order.currentLat)
      if (this.isValidLngLat(lng, lat)) {
        return { lng, lat }
      }
      return GUIZHOU_MUSEUM_WGS84
    },
    pickupPosition() {
      const lng = this.numberValue(this.order.beginLng ?? this.order.startLng ?? this.order.pickupLng)
      const lat = this.numberValue(this.order.beginLat ?? this.order.startLat ?? this.order.pickupLat)
      if (this.isValidLngLat(lng, lat)) {
        return { lng, lat }
      }
      return this.destinationPosition
    },
    destinationPosition() {
      const lng = this.numberValue(
        this.isShuttleService
          ? (this.order.endLng ?? this.order.destinationLng ?? this.order.targetLng)
          : (this.order.serviceLng ?? this.order.destinationLng ?? this.order.targetLng)
      )
      const lat = this.numberValue(
        this.isShuttleService
          ? (this.order.endLat ?? this.order.destinationLat ?? this.order.targetLat)
          : (this.order.serviceLat ?? this.order.destinationLat ?? this.order.targetLat)
      )
      if (this.isValidLngLat(lng, lat)) {
        return { lng, lat }
      }
      return GUIZHOU_MUSEUM_WGS84
    },
    routeTargetPosition() {
      if (!this.isShuttleService) return this.destinationPosition
      return this.isHeadingToPickup
        ? this.pickupPosition
        : this.destinationPosition
    },
    isHeadingToPickup() {
      if (!this.isShuttleService) return false
      if (this.demoMode) return ['TO_PICKUP', 'WAITING_PICKUP'].includes(this.demoPhase)
      return ['ACCEPTED', 'DEPARTED'].includes(this.order.workflowStatus || 'ACCEPTED')
    },
    destinationLabel() {
      if (this.isShuttleService) {
        if (this.isHeadingToPickup) {
          return this.order.beginAddress || '接驳起点待选择'
        }
        return this.order.endAddress || '接驳终点待选择'
      }
      return this.order.serviceAddress || '服务地址待选择'
    },
    distanceMeters() {
      if (this.demoMode && Number.isFinite(this.demoRouteDistanceMeters)) {
        return this.calculateRouteRemainingMeters(this.displayedRobotPosition, this.demoRoutePoints)
      }
      if (Number.isFinite(this.plannedRouteDistanceMeters)) {
        return this.plannedRouteDistanceMeters
      }
      return this.calculateDistanceMeters(this.displayedRobotPosition, this.routeTargetPosition)
    },
    distanceLabel() {
      if (this.demoRoutePoints.length > 1 || this.plannedRoutePoints.length > 1) {
        if (this.isShuttleService) {
          return this.isHeadingToPickup ? '距接驳起点' : '距接驳终点'
        }
        return '道路剩余距离'
      }
      return '直线距离'
    },
    remainingDistanceText() {
      const meters = this.distanceMeters
      if (!Number.isFinite(meters)) return '等待GPS'
      if (meters < 1000) return `${Math.round(meters)} 米`
      return `${(meters / 1000).toFixed(2)} 公里`
    },
    remainingEtaText() {
      const meters = this.distanceMeters
      if (!Number.isFinite(meters)) return '等待GPS'
      if (this.demoPhase === 'WAITING_PICKUP') return '等待 OK'
      if (this.demoMode && this.demoRunning && Number.isFinite(this.demoRouteDistanceMeters) && this.demoRouteDistanceMeters > 0) {
        const progress = Math.max(0, Math.min(1, 1 - meters / this.demoRouteDistanceMeters))
        const seconds = Math.max(1, Math.round(DEMO_ROUTE_DURATION * (1 - progress) / 1000))
        return seconds < 60 ? `演示约 ${seconds} 秒` : `演示约 ${Math.ceil(seconds / 60)} 分钟`
      }
      if (!this.demoMode && Number.isFinite(this.plannedRouteDurationSeconds) && this.plannedRouteDurationSeconds > 0) {
        const minutes = Math.max(1, Math.ceil(this.plannedRouteDurationSeconds / 60))
        return minutes < 60 ? `约 ${minutes} 分钟` : `约 ${Math.floor(minutes / 60)} 小时 ${minutes % 60} 分钟`
      }
      const speedKmh = Math.max(8, Math.abs(Number(this.order.robotSpeed || 0)) || 12)
      const minutes = Math.max(1, Math.ceil(meters / (speedKmh * 1000 / 60)))
      if (minutes < 60) return `约 ${minutes} 分钟`
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      return rest ? `约 ${hours} 小时 ${rest} 分钟` : `约 ${hours} 小时`
    },
    routeHint() {
      if (this.demoMode) {
        if (this.demoRouteLoading) return this.demoPhase === 'TO_DESTINATION'
          ? '正在规划接驳第二段路线：从起点前往终点。'
          : '正在规划路线，车辆会从当前 GPS 坐标出发。'
        if (this.demoRouteError) return `${this.demoRouteError} 当前使用直线演示路线。`
        if (this.demoPhase === 'WAITING_PICKUP') return '车辆已到达接驳起点。这里模拟乘客上车确认，点击地图里的 OK 后再前往终点。'
        if (this.demoRunning) return this.demoPhase === 'TO_PICKUP'
          ? '接驳测试中：车辆正在从当前位置前往用户起点。'
          : '接驳测试中：车辆正在从起点前往终点。'
        return '测试执行已完成。刷新页面后会重新读取车辆最新 GPS 经纬度。'
      }
      if (this.hasRealRobotPosition) {
        if (this.plannedRouteLoading) return '正在规划道路路线，用于展示更接近真实行驶距离的 ETA。'
        if (this.plannedRoutePoints.length > 1) {
          if (this.isShuttleService) {
            return this.isHeadingToPickup
              ? '已按道路路线计算：车辆正在前往接驳起点。'
              : '已按道路路线计算：车辆正在前往接驳终点。'
          }
          return '已按道路路线计算车辆到服务点的距离和预计到达时间。'
        }
        return `已接入 TDengine GPS 原始坐标：${this.order.robotLocationDeviceName || this.order.robotName || '车辆'}。当前地图按 WGS84 展示，不做高德/腾讯坐标纠偏。`
      }
      return `暂未收到车辆GPS，先用贵州省博物馆作为模拟起点展示。`
    },
    robotCoordinateText() {
      return `${this.displayedRobotPosition.lng.toFixed(6)}, ${this.displayedRobotPosition.lat.toFixed(6)}`
    },
    locationSourceText() {
      if (this.demoMode) {
        if (this.demoPhase === 'WAITING_PICKUP') return '接驳起点 · 等待 OK'
        return this.demoRunning ? '测试路线 · 演示中' : '测试路线 · 已完成'
      }
      if (this.hasRealRobotPosition) {
        const device = this.order.robotLocationDeviceName || this.order.robotName || 'TDengine'
        return `${device}${this.order.robotLocationOnline === false ? ' · 非实时' : ' · 实时'}`
      }
      return '模拟坐标'
    },
    speedHeadingText() {
      if (this.demoMode) {
        const heading = Number.isFinite(this.demoRobotHeading) ? `${this.demoRobotHeading.toFixed(0)}°` : '-'
        const speed = this.demoRunning && Number.isFinite(this.demoRouteDistanceMeters)
          ? `${Math.max(1, (this.demoRouteDistanceMeters / (DEMO_ROUTE_DURATION / 1000) * 3.6).toFixed(1))} km/h`
          : '0 km/h'
        return `${speed} / ${heading}`
      }
      const speed = this.order.robotSpeed === null || this.order.robotSpeed === undefined ? '-' : `${this.order.robotSpeed} km/h`
      const heading = this.order.robotHeading === null || this.order.robotHeading === undefined ? '-' : `${Number(this.order.robotHeading).toFixed(0)}°`
      return `${speed} / ${heading}`
    },
    vehicleStatusText() {
      const map = {
        ACCEPTED: '待出发',
        DEPARTED: '前往服务点',
        ARRIVED: '已到达',
        START_REQUESTED: '等待开始',
        STARTED: '服务中',
        END_REQUESTED: '等待结束',
        FINISHED: '已完成'
      }
      return map[this.order.workflowStatus] || '在线'
    },
    centerWorld() {
      return this.lngLatToWorld(this.center.lng, this.center.lat, this.zoom)
    },
    visibleTiles() {
      const halfWidth = this.viewport.width / 2
      const halfHeight = this.viewport.height / 2
      const minX = Math.floor((this.centerWorld.x - halfWidth) / TILE_SIZE) - 1
      const maxX = Math.floor((this.centerWorld.x + halfWidth) / TILE_SIZE) + 1
      const minY = Math.floor((this.centerWorld.y - halfHeight) / TILE_SIZE) - 1
      const maxY = Math.floor((this.centerWorld.y + halfHeight) / TILE_SIZE) + 1
      const maxTile = Math.pow(2, this.zoom)
      const tiles = []

      for (let x = minX; x <= maxX; x += 1) {
        for (let y = minY; y <= maxY; y += 1) {
          if (y < 0 || y >= maxTile) continue
          const wrappedX = ((x % maxTile) + maxTile) % maxTile
          const left = x * TILE_SIZE - this.centerWorld.x + halfWidth
          const top = y * TILE_SIZE - this.centerWorld.y + halfHeight
          tiles.push({
            key: `${this.tileProviderIndex}-${this.zoom}-${x}-${y}`,
            x: wrappedX,
            y,
            z: this.zoom,
            fallbackIndex: this.tileProviderIndex,
            url: this.tileUrl(this.zoom, wrappedX, y, this.tileProviderIndex),
            style: {
              width: `${TILE_SIZE}px`,
              height: `${TILE_SIZE}px`,
              transform: `translate(${left}px, ${top}px)`
            }
          })
        }
      }
      return tiles
    },
    robotScreenPoint() {
      return this.lngLatToScreen(this.displayedRobotPosition)
    },
    targetScreenPoint() {
      return this.lngLatToScreen(this.routeTargetPosition)
    },
    pickupScreenPoint() {
      return this.lngLatToScreen(this.pickupPosition)
    },
    destinationScreenPoint() {
      return this.lngLatToScreen(this.destinationPosition)
    },
    routeScreenPoints() {
      return this.displayedRoutePoints.map(point => this.lngLatToScreen(point)).filter(Boolean)
    },
    routePolyline() {
      return this.routeScreenPoints.map(point => `${point.x},${point.y}`).join(' ')
    },
    routeDeviationAlert() {
      if (this.demoMode || !this.hasRealRobotPosition || this.plannedRoutePoints.length < 2) return ''
      if (!['DEPARTED', 'STARTED'].includes(this.order.workflowStatus || 'ACCEPTED')) return ''
      const distance = this.distanceToRouteMeters(this.displayedRobotPosition, this.plannedRoutePoints)
      if (!Number.isFinite(distance) || distance <= 350) return ''
      return `车辆可能偏离当前道路路线约 ${Math.round(distance)} 米，请关注现场运行状态`
    },
    robotMarkerStyle() {
      return this.markerStyle(this.robotScreenPoint)
    },
    targetMarkerStyle() {
      return this.markerStyle(this.targetScreenPoint, ' rotate(-45deg)')
    },
    pickupMarkerStyle() {
      return this.endpointMarkerStyle(this.pickupScreenPoint)
    },
    destinationMarkerStyle() {
      return this.endpointMarkerStyle(this.destinationScreenPoint)
    },
    robotHeading() {
      if (Number.isFinite(this.demoRobotHeading)) {
        return this.demoRobotHeading
      }
      const heading = this.numberValue(this.order.robotHeading)
      if (heading !== null) {
        return heading
      }
      return this.bearingBetween(this.displayedRobotPosition, this.routeTargetPosition)
    },
    demoButtonText() {
      if (this.demoRouteLoading) return '获取路线中...'
      if (this.demoRunning) return this.demoPhase === 'TO_PICKUP' ? '前往起点中' : '前往终点中'
      if (this.demoPhase === 'WAITING_PICKUP') return '等待起点确认'
      if (this.demoMode) return '重新测试执行'
      return '测试执行'
    },
    demoStatusText() {
      if (this.demoRouteLoading) return '正在规划真实道路路线'
      if (this.demoPhase === 'WAITING_PICKUP') return '已到达接驳起点，等待 OK'
      if (this.demoRunning) return this.demoPhase === 'TO_PICKUP'
        ? '接驳第一段：车辆前往起点'
        : '接驳第二段：车辆前往终点'
      return '测试路线已完成'
    }
  },
  watch: {
    order: {
      deep: true,
      handler() {
        if (this.demoMode) return
        this.queuePlannedRoute()
        if (!this.userAdjustedMap) {
          this.focusRoute()
        }
      }
    }
  },
  mounted() {
    this.measureViewport()
    this.focusRoute()
    this.queuePlannedRoute()
    window.addEventListener('resize', this.measureViewport)
    if (window.ResizeObserver && this.$refs.mapContainer) {
      this.resizeObserver = new ResizeObserver(this.measureViewport)
      this.resizeObserver.observe(this.$refs.mapContainer)
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.measureViewport)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.panRaf) {
      window.cancelAnimationFrame(this.panRaf)
      this.panRaf = null
    }
    if (this.wheelRaf) {
      window.cancelAnimationFrame(this.wheelRaf)
      this.wheelRaf = null
    }
    this.stopDemoAnimation()
  },
  methods: {
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    tileUrl(z, x, y, providerIndex = this.tileProviderIndex) {
      const provider = TILE_PROVIDERS[providerIndex] || TILE_PROVIDERS[0]
      return provider.url(z, x, y)
    },
    routeKey(start, end) {
      return [
        start.lng.toFixed(5),
        start.lat.toFixed(5),
        end.lng.toFixed(5),
        end.lat.toFixed(5)
      ].join(',')
    },
    async queuePlannedRoute() {
      if (this.demoMode || this.plannedRouteLoading) return
      const start = this.displayedRobotPosition
      const end = this.routeTargetPosition
      if (!this.isValidLngLat(start.lng, start.lat) || !this.isValidLngLat(end.lng, end.lat)) return
      const key = this.routeKey(start, end)
      if (key === this.plannedRouteKey) return
      this.plannedRouteKey = key
      this.plannedRouteLoading = true
      this.plannedRouteError = ''
      try {
        const route = await this.fetchRoadRoute(start, end)
        if (this.routeKey(this.displayedRobotPosition, this.routeTargetPosition) !== key || this.demoMode) return
        this.plannedRoutePoints = route.points
        this.plannedRouteDistanceMeters = route.distance
        this.plannedRouteDurationSeconds = route.duration
      } catch (error) {
        if (this.routeKey(this.displayedRobotPosition, this.routeTargetPosition) !== key || this.demoMode) return
        this.plannedRouteError = '道路路线暂不可用'
        this.plannedRoutePoints = []
        this.plannedRouteDistanceMeters = null
        this.plannedRouteDurationSeconds = null
      } finally {
        if (this.plannedRouteKey === key) this.plannedRouteLoading = false
      }
    },
    async startDemoRoute() {
      if (!this.canStartDemo || this.demoRouteLoading) return
      const start = { ...this.robotPosition }
      const end = this.isShuttleService ? { ...this.pickupPosition } : { ...this.destinationPosition }
      this.stopDemoAnimation()
      this.demoMode = true
      this.demoRunning = false
      this.demoRouteLoading = true
      this.demoRouteError = ''
      this.demoRobotPosition = { ...start }
      this.demoRobotHeading = this.bearingBetween(start, end)
      this.demoPhase = this.isShuttleService ? 'TO_PICKUP' : 'TO_DESTINATION'
      this.demoRoutePoints = [start, end]
      this.demoRouteDistanceMeters = this.calculateDistanceMeters(start, end)
      this.demoRouteDurationSeconds = null
      this.plannedRoutePoints = []
      this.plannedRouteDistanceMeters = null
      this.plannedRouteDurationSeconds = null
      this.userAdjustedMap = false
      await this.prepareDemoSegment(start, end)
      this.runDemoSegment()
    },
    async continueDemoToDestination() {
      if (this.demoPhase !== 'WAITING_PICKUP' || this.demoRouteLoading) return
      const start = { ...this.pickupPosition }
      const end = { ...this.destinationPosition }
      this.stopDemoAnimation()
      this.demoRunning = false
      this.demoRouteLoading = true
      this.demoRouteError = ''
      this.demoPhase = 'TO_DESTINATION'
      this.demoRobotPosition = { ...start }
      this.demoRobotHeading = this.bearingBetween(start, end)
      this.demoRoutePoints = [start, end]
      this.demoRouteDistanceMeters = this.calculateDistanceMeters(start, end)
      this.demoRouteDurationSeconds = null
      this.userAdjustedMap = false
      await this.prepareDemoSegment(start, end)
      this.runDemoSegment()
    },
    async prepareDemoSegment(start, end) {
      try {
        const route = await this.fetchRoadRoute(start, end)
        if (route && route.points && route.points.length > 1) {
          this.demoRoutePoints = route.points
          this.demoRouteDistanceMeters = route.distance
          this.demoRouteDurationSeconds = route.duration
          this.demoRobotHeading = this.bearingBetween(route.points[0], route.points[1])
        }
      } catch (error) {
        this.demoRouteError = '真实道路路线服务暂不可用。'
        this.demoRoutePoints = this.buildFallbackRoute(start, end)
        this.demoRouteDistanceMeters = this.calculateRouteLength(this.demoRoutePoints)
      } finally {
        this.demoRouteLoading = false
      }
    },
    runDemoSegment() {
      this.focusRoute()
      this.demoRunning = true
      this.demoStartTs = window.performance ? window.performance.now() : Date.now()
      this.demoAnimationFrame = window.requestAnimationFrame(this.stepDemoRoute)
    },
    async fetchRoadRoute(start, end) {
      const startLng = encodeURIComponent(start.lng)
      const startLat = encodeURIComponent(start.lat)
      const endLng = encodeURIComponent(end.lng)
      const endLat = encodeURIComponent(end.lat)
      const url = `https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=full&geometries=geojson&steps=false`
      const response = await window.fetch(url)
      if (!response.ok) {
        throw new Error(`route http ${response.status}`)
      }
      const data = await response.json()
      const route = data && data.routes && data.routes[0]
      const coordinates = route && route.geometry && route.geometry.coordinates
      if (!Array.isArray(coordinates) || coordinates.length < 2) {
        throw new Error('empty route')
      }
      const points = coordinates
        .map(item => ({ lng: Number(item[0]), lat: Number(item[1]) }))
        .filter(point => this.isValidLngLat(point.lng, point.lat))
      if (points.length < 2) {
        throw new Error('invalid route')
      }
      return {
        points: this.limitRoutePoints(points),
        distance: Number(route.distance) || this.calculateRouteLength(points),
        duration: Number(route.duration) || null
      }
    },
    limitRoutePoints(points) {
      if (!Array.isArray(points) || points.length <= 260) return points
      const limited = [points[0]]
      const step = Math.ceil(points.length / 240)
      for (let index = step; index < points.length - 1; index += step) {
        limited.push(points[index])
      }
      limited.push(points[points.length - 1])
      return limited
    },
    buildFallbackRoute(start, end) {
      const points = []
      const steps = 36
      for (let index = 0; index <= steps; index += 1) {
        const progress = index / steps
        points.push({
          lng: start.lng + (end.lng - start.lng) * progress,
          lat: start.lat + (end.lat - start.lat) * progress
        })
      }
      return points
    },
    stopDemoAnimation() {
      if (this.demoAnimationFrame) {
        window.cancelAnimationFrame(this.demoAnimationFrame)
        this.demoAnimationFrame = null
      }
    },
    stepDemoRoute(timestamp) {
      if (!this.demoRunning || this.demoRoutePoints.length < 2) return
      const now = timestamp || (window.performance ? window.performance.now() : Date.now())
      const progress = Math.max(0, Math.min(1, (now - this.demoStartTs) / DEMO_ROUTE_DURATION))
      const position = this.pointOnRoute(this.demoRoutePoints, progress)
      this.demoRobotPosition = position.point
      this.demoRobotHeading = position.heading
      if (!this.userAdjustedMap) {
        this.center = { ...position.point }
      }
      if (progress >= 1) {
        this.demoRunning = false
        this.demoAnimationFrame = null
        if (this.isShuttleService && this.demoPhase === 'TO_PICKUP') {
          this.demoPhase = 'WAITING_PICKUP'
          this.demoRobotPosition = { ...this.pickupPosition }
          this.demoRoutePoints = []
          this.demoRouteDistanceMeters = 0
          this.center = { ...this.pickupPosition }
          return
        }
        this.demoPhase = 'FINISHED'
        this.demoRobotPosition = { ...this.destinationPosition }
        return
      }
      this.demoAnimationFrame = window.requestAnimationFrame(this.stepDemoRoute)
    },
    pointOnRoute(points, progress) {
      const total = this.calculateRouteLength(points)
      if (!Number.isFinite(total) || total <= 0) {
        return {
          point: { ...points[points.length - 1] },
          heading: this.bearingBetween(points[0], points[points.length - 1])
        }
      }
      const targetDistance = total * Math.max(0, Math.min(1, progress))
      let walked = 0
      for (let index = 1; index < points.length; index += 1) {
        const from = points[index - 1]
        const to = points[index]
        const segment = this.calculateDistanceMeters(from, to)
        if (!Number.isFinite(segment) || segment <= 0) continue
        if (walked + segment >= targetDistance) {
          const ratio = (targetDistance - walked) / segment
          const point = {
            lng: from.lng + (to.lng - from.lng) * ratio,
            lat: from.lat + (to.lat - from.lat) * ratio
          }
          return {
            point,
            heading: this.bearingBetween(from, to)
          }
        }
        walked += segment
      }
      return {
        point: { ...points[points.length - 1] },
        heading: this.bearingBetween(points[points.length - 2], points[points.length - 1])
      }
    },
    calculateRouteLength(points) {
      if (!Array.isArray(points) || points.length < 2) return 0
      return points.slice(1).reduce((total, point, index) => {
        const segment = this.calculateDistanceMeters(points[index], point)
        return total + (Number.isFinite(segment) ? segment : 0)
      }, 0)
    },
    calculateRouteRemainingMeters(position, points) {
      if (!position || !Array.isArray(points) || points.length < 2) {
        return this.calculateDistanceMeters(position, this.routeTargetPosition)
      }
      let nearestIndex = 0
      let nearestDistance = Infinity
      points.forEach((point, index) => {
        const distance = this.calculateDistanceMeters(position, point)
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestIndex = index
        }
      })
      const remainingPoints = [position, ...points.slice(Math.min(nearestIndex + 1, points.length - 1))]
      return this.calculateRouteLength(remainingPoints)
    },
    distanceToRouteMeters(position, points) {
      if (!position || !Array.isArray(points) || points.length < 2) return null
      let minDistance = Infinity
      for (let index = 1; index < points.length; index += 1) {
        const distance = this.distanceToSegmentMeters(position, points[index - 1], points[index])
        if (Number.isFinite(distance) && distance < minDistance) {
          minDistance = distance
        }
      }
      return minDistance
    },
    distanceToSegmentMeters(point, start, end) {
      if (!point || !start || !end) return null
      const refLat = point.lat * Math.PI / 180
      const metersPerDegreeLat = 111320
      const metersPerDegreeLng = 111320 * Math.cos(refLat)
      const px = point.lng * metersPerDegreeLng
      const py = point.lat * metersPerDegreeLat
      const sx = start.lng * metersPerDegreeLng
      const sy = start.lat * metersPerDegreeLat
      const ex = end.lng * metersPerDegreeLng
      const ey = end.lat * metersPerDegreeLat
      const dx = ex - sx
      const dy = ey - sy
      const lengthSquared = dx * dx + dy * dy
      if (lengthSquared <= 0) {
        return Math.sqrt((px - sx) * (px - sx) + (py - sy) * (py - sy))
      }
      const ratio = Math.max(0, Math.min(1, ((px - sx) * dx + (py - sy) * dy) / lengthSquared))
      const closestX = sx + ratio * dx
      const closestY = sy + ratio * dy
      return Math.sqrt((px - closestX) * (px - closestX) + (py - closestY) * (py - closestY))
    },
    handleTileLoad() {
      if (this.tileErrorCount > 0) {
        this.tileErrorCount = Math.max(0, this.tileErrorCount - 1)
      }
    },
    handleTileError(tile, event) {
      const img = event && event.target
      if (!img) return
      const nextIndex = Number(img.dataset.fallbackIndex || tile.fallbackIndex || 0) + 1
      if (nextIndex < TILE_PROVIDERS.length) {
        img.dataset.fallbackIndex = String(nextIndex)
        img.src = this.tileUrl(tile.z, tile.x, tile.y, nextIndex)
        return
      }
      this.tileErrorCount += 1
      if (this.tileErrorCount > 8 && this.tileProviderIndex < TILE_PROVIDERS.length - 1) {
        this.tileProviderIndex += 1
        this.tileErrorCount = 0
      }
    },
    isValidLngLat(lng, lat) {
      return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 85
    },
    calculateDistanceMeters(start, end) {
      if (!start || !end) return null
      const toRad = value => value * Math.PI / 180
      const earthRadius = 6371000
      const dLat = toRad(end.lat - start.lat)
      const dLng = toRad(end.lng - start.lng)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(start.lat)) * Math.cos(toRad(end.lat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    bearingBetween(from, to) {
      if (!from || !to) return 0
      const toRad = value => value * Math.PI / 180
      const toDeg = value => value * 180 / Math.PI
      const fromLat = toRad(from.lat)
      const toLat = toRad(to.lat)
      const dLng = toRad(to.lng - from.lng)
      const y = Math.sin(dLng) * Math.cos(toLat)
      const x = Math.cos(fromLat) * Math.sin(toLat) -
        Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng)
      return (toDeg(Math.atan2(y, x)) + 360) % 360
    },
    lngLatToWorld(lng, lat, zoom) {
      const scale = TILE_SIZE * Math.pow(2, zoom)
      const clippedLat = Math.max(Math.min(lat, 85.05112878), -85.05112878)
      const sinLat = Math.sin(clippedLat * Math.PI / 180)
      return {
        x: (lng + 180) / 360 * scale,
        y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
      }
    },
    lngLatToScreen(point) {
      if (!point) return null
      const world = this.lngLatToWorld(point.lng, point.lat, this.zoom)
      return {
        x: world.x - this.centerWorld.x + this.viewport.width / 2,
        y: world.y - this.centerWorld.y + this.viewport.height / 2
      }
    },
    markerStyle(point, extraTransform = '') {
      if (!point) {
        return { display: 'none' }
      }
      return {
        transform: `translate(${point.x}px, ${point.y}px)${extraTransform}`
      }
    },
    endpointMarkerStyle(point) {
      if (!point) {
        return { display: 'none' }
      }
      return {
        transform: `translate(${point.x}px, ${point.y}px)`
      }
    },
    measureViewport() {
      const element = this.$refs.mapContainer
      if (!element) return
      const rect = element.getBoundingClientRect()
      const nextViewport = {
        width: Math.max(320, Math.round(rect.width || DEFAULT_VIEWPORT.width)),
        height: Math.max(300, Math.round(rect.height || DEFAULT_VIEWPORT.height))
      }
      if (nextViewport.width === this.viewport.width && nextViewport.height === this.viewport.height) {
        return
      }
      this.viewport = {
        ...nextViewport
      }
    },
    focusRoute() {
      const start = this.displayedRobotPosition
      const end = this.isShuttleService ? this.destinationPosition : this.routeTargetPosition
      this.zoom = this.zoomForBounds(start, end)
      const startWorld = this.lngLatToWorld(start.lng, start.lat, this.zoom)
      const endWorld = this.lngLatToWorld(end.lng, end.lat, this.zoom)
      this.center = this.worldToLngLat((startWorld.x + endWorld.x) / 2, (startWorld.y + endWorld.y) / 2, this.zoom)
      this.userAdjustedMap = false
    },
    focusRobot() {
      this.center = { ...this.displayedRobotPosition }
      this.userAdjustedMap = false
    },
    zoomIn() {
      this.setZoom(this.zoom + 1, this.viewportCenterPoint())
    },
    zoomOut() {
      this.setZoom(this.zoom - 1, this.viewportCenterPoint())
    },
    viewportCenterPoint() {
      return {
        x: this.viewport.width / 2,
        y: this.viewport.height / 2
      }
    },
    setZoom(nextZoom, anchorPoint = null) {
      const oldZoom = this.zoom
      const zoom = Math.max(3, Math.min(19, Math.round(nextZoom)))
      if (zoom === oldZoom) return

      if (anchorPoint) {
        const before = this.screenToLngLat(anchorPoint.x, anchorPoint.y, oldZoom, this.center)
        this.zoom = zoom
        const worldAfter = this.lngLatToWorld(before.lng, before.lat, zoom)
        const centerWorld = {
          x: worldAfter.x - anchorPoint.x + this.viewport.width / 2,
          y: worldAfter.y - anchorPoint.y + this.viewport.height / 2
        }
        this.center = this.worldToLngLat(centerWorld.x, centerWorld.y, zoom)
      } else {
        this.zoom = zoom
      }
      this.userAdjustedMap = true
    },
    normalizeWheelDelta(event) {
      if (event.deltaMode === 1) return event.deltaY * 16
      if (event.deltaMode === 2) return event.deltaY * this.viewport.height
      return event.deltaY
    },
    handleWheel(event) {
      event.preventDefault()
      const rect = this.$refs.mapContainer.getBoundingClientRect()
      this.wheelAnchor = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      this.wheelDeltaY += this.normalizeWheelDelta(event)
      this.userAdjustedMap = true
      if (!this.wheelRaf) {
        this.wheelRaf = window.requestAnimationFrame(() => this.flushWheelZoom())
      }
    },
    flushWheelZoom() {
      this.wheelRaf = null
      const threshold = 80
      if (Math.abs(this.wheelDeltaY) < threshold) {
        return
      }
      const direction = this.wheelDeltaY > 0 ? 1 : -1
      const rawSteps = Math.floor(Math.abs(this.wheelDeltaY) / threshold)
      const steps = Math.min(2, rawSteps) * direction
      this.wheelDeltaY -= steps * threshold
      this.setZoom(this.zoom - steps, this.wheelAnchor || this.viewportCenterPoint())
      if (Math.abs(this.wheelDeltaY) >= threshold) {
        this.wheelRaf = window.requestAnimationFrame(() => this.flushWheelZoom())
      }
    },
    handleDoubleClick(event) {
      const rect = this.$refs.mapContainer.getBoundingClientRect()
      this.setZoom(this.zoom + 1, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      })
    },
    startPan(event) {
      if (event.button !== undefined && event.button !== 0) return
      event.preventDefault()
      this.isPanning = true
      this.activePointerId = event.pointerId
      this.lastPanPoint = { x: event.clientX, y: event.clientY }
      this.pendingPanDelta = { x: 0, y: 0 }
      this.userAdjustedMap = true
      if (event.currentTarget && event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
    },
    movePan(event) {
      if (!this.isPanning || event.pointerId !== this.activePointerId || !this.lastPanPoint) return
      event.preventDefault()
      const dx = event.clientX - this.lastPanPoint.x
      const dy = event.clientY - this.lastPanPoint.y
      this.lastPanPoint = { x: event.clientX, y: event.clientY }
      if (dx === 0 && dy === 0) return
      this.pendingPanDelta.x += dx
      this.pendingPanDelta.y += dy
      if (!this.panRaf) {
        this.panRaf = window.requestAnimationFrame(() => this.flushPan())
      }
    },
    flushPan() {
      this.panRaf = null
      const dx = this.pendingPanDelta.x
      const dy = this.pendingPanDelta.y
      this.pendingPanDelta = { x: 0, y: 0 }
      if (dx === 0 && dy === 0) return
      const centerWorld = this.lngLatToWorld(this.center.lng, this.center.lat, this.zoom)
      this.center = this.worldToLngLat(centerWorld.x - dx, centerWorld.y - dy, this.zoom)
    },
    endPan(event) {
      if (event && event.pointerId !== undefined && this.activePointerId !== null && event.pointerId !== this.activePointerId) {
        return
      }
      if (this.panRaf) {
        window.cancelAnimationFrame(this.panRaf)
        this.panRaf = null
        this.flushPan()
      }
      if (event && event.currentTarget && event.currentTarget.releasePointerCapture && this.activePointerId !== null) {
        try {
          event.currentTarget.releasePointerCapture(this.activePointerId)
        } catch (error) {
          // The pointer may already be released by the browser.
        }
      }
      this.isPanning = false
      this.activePointerId = null
      this.lastPanPoint = null
    },
    screenToLngLat(x, y, zoom = this.zoom, center = this.center) {
      const centerWorld = this.lngLatToWorld(center.lng, center.lat, zoom)
      return this.worldToLngLat(
        centerWorld.x + x - this.viewport.width / 2,
        centerWorld.y + y - this.viewport.height / 2,
        zoom
      )
    },
    worldToLngLat(x, y, zoom) {
      const scale = TILE_SIZE * Math.pow(2, zoom)
      const lng = x / scale * 360 - 180
      const n = Math.PI - 2 * Math.PI * y / scale
      const lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
      return {
        lng: Math.max(-180, Math.min(180, lng)),
        lat: Math.max(-85, Math.min(85, lat))
      }
    },
    zoomForDistance(distance) {
      if (!Number.isFinite(distance)) return 14
      if (distance < 300) return 17
      if (distance < 800) return 16
      if (distance < 1600) return 15
      if (distance < 3500) return 14
      if (distance < 8000) return 13
      if (distance < 18000) return 12
      if (distance < 50000) return 10
      return 8
    },
    zoomForBounds(start, end) {
      if (!start || !end) return 14
      const startWorld = this.lngLatToWorld(start.lng, start.lat, 0)
      const endWorld = this.lngLatToWorld(end.lng, end.lat, 0)
      const dx = Math.max(Math.abs(startWorld.x - endWorld.x), 0.000001)
      const dy = Math.max(Math.abs(startWorld.y - endWorld.y), 0.000001)
      const availableWidth = Math.max(160, this.viewport.width - 144)
      const availableHeight = Math.max(120, this.viewport.height - 120)
      const fitZoom = Math.floor(Math.min(
        Math.log2(availableWidth / dx),
        Math.log2(availableHeight / dy)
      ))
      return Math.max(3, Math.min(18, fitZoom))
    }
  }
}
</script>

<style scoped>
.order-map-card {
  display: grid;
  gap: 14px;
  margin: 18px 0;
  padding: 16px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(79, 198, 255, 0.12), rgba(255, 176, 82, 0.1)),
    rgba(5, 13, 23, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.order-map-head,
.order-map-layout,
.eta-grid {
  display: grid;
  gap: 12px;
}

.order-map-head {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}

.order-map-head span {
  display: block;
  margin-bottom: 6px;
  color: rgba(255, 205, 145, 0.86);
  font-size: 12px;
  letter-spacing: 0.2em;
}

.order-map-head strong {
  display: block;
  font-size: 22px;
}

.order-map-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.order-map-head button,
.test-run-btn {
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  color: #06111e;
  border: none;
  font-weight: 800;
  background: linear-gradient(135deg, #8ed7ff, #ffd08a);
  cursor: pointer;
}

.test-run-btn {
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #8ed7ff);
}

.test-run-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.order-map-layout {
  grid-template-columns: 360px minmax(0, 1fr);
  align-items: stretch;
}

.order-map-copy {
  display: grid;
  gap: 12px;
}

.eta-card,
.eta-grid div {
  padding: 14px;
  border-radius: 18px;
  background: rgba(6, 17, 30, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.09);
}

.eta-card.primary {
  min-height: 132px;
  background:
    linear-gradient(135deg, rgba(255, 138, 42, 0.16), rgba(143, 241, 199, 0.1)),
    rgba(6, 17, 30, 0.72);
}

.eta-card span,
.eta-grid span {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 246, 234, 0.58);
  font-size: 12px;
}

.eta-card strong {
  display: block;
  color: #fff6ea;
  font-size: 34px;
}

.eta-card p {
  margin: 10px 0 0;
  color: rgba(255, 246, 234, 0.7);
  line-height: 1.7;
}

.eta-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.eta-grid strong {
  display: block;
  color: #fff6ea;
  line-height: 1.5;
  word-break: break-word;
}

.order-map-panel {
  position: relative;
  min-height: 360px;
  overflow: hidden;
  border-radius: 20px;
  background: #d7e1dc;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.wgs-map-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 360px;
  overflow: hidden;
  background: #d7e1dc;
  cursor: grab;
  user-select: none;
  touch-action: none;
  overscroll-behavior: contain;
  -webkit-user-select: none;
}

.wgs-map-canvas.is-panning {
  cursor: grabbing;
}

.map-tile {
  position: absolute;
  left: 0;
  top: 0;
  will-change: transform;
}

.map-tile img {
  display: block;
  width: 256px;
  height: 256px;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.route-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.route-line {
  fill: none;
  stroke: #12a7ff;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.route-line-shadow {
  stroke: rgba(4, 10, 18, 0.42);
  stroke-width: 9;
  stroke-dasharray: none;
}

.robot-marker-wrap,
.target-marker,
.endpoint-marker {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 5;
  transform-origin: center;
  pointer-events: none;
}

.robot-marker-wrap {
  margin-left: -23px;
  margin-top: -17px;
  transition: transform 0.7s ease;
}

.wgs-map-canvas.is-panning .robot-marker-wrap {
  transition: none;
}

.target-marker {
  width: 24px;
  height: 24px;
  margin-left: -12px;
  margin-top: -24px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  background: #49c8ff;
  box-shadow: 0 8px 20px rgba(73, 200, 255, 0.34);
}

.target-marker::after {
  content: '';
  position: absolute;
  left: 7px;
  top: 7px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
}

.endpoint-marker {
  display: grid;
  grid-template-columns: auto auto;
  gap: 6px;
  align-items: center;
  margin-left: -13px;
  margin-top: -33px;
  padding: 5px 8px 5px 5px;
  border-radius: 999px;
  color: #06111e;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 26px rgba(5, 13, 23, 0.24);
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.endpoint-marker b {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #06111e;
  font-size: 13px;
}

.endpoint-marker span {
  line-height: 1;
}

.pickup-marker b {
  background: #8ff1c7;
}

.destination-marker b {
  background: #49c8ff;
}

.destination-marker {
  margin-top: -4px;
}

.map-legend {
  position: absolute;
  left: 14px;
  bottom: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(6, 17, 30, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.14);
  z-index: 8;
}

.map-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 246, 234, 0.82);
  font-size: 12px;
}

.map-attribution {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 7;
  padding: 5px 8px;
  border-radius: 999px;
  color: rgba(5, 13, 23, 0.7);
  background: rgba(255, 255, 255, 0.76);
  font-size: 11px;
}

.tile-warning,
.route-deviation-warning {
  position: absolute;
  left: 50%;
  top: 14px;
  z-index: 9;
  max-width: calc(100% - 120px);
  padding: 8px 12px;
  border-radius: 999px;
  color: #06111e;
  background: rgba(255, 208, 138, 0.92);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  font-weight: 800;
  transform: translateX(-50%);
}

.route-deviation-warning {
  top: 54px;
  color: #fff6ea;
  background: rgba(255, 102, 94, 0.92);
}

.demo-route-badge {
  position: absolute;
  left: 14px;
  top: 14px;
  z-index: 9;
  max-width: calc(100% - 110px);
  padding: 8px 12px;
  border-radius: 999px;
  color: #06111e;
  background: rgba(255, 208, 138, 0.94);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  font-weight: 900;
}

.demo-route-badge.running {
  background: linear-gradient(135deg, #8ff1c7, #8ed7ff);
}

.map-zoom-controls {
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: 9;
  display: grid;
  gap: 8px;
}

.map-zoom-controls button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(6, 17, 30, 0.16);
  border-radius: 10px;
  color: #06111e;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  font-size: 20px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
}

.map-zoom-controls button:hover {
  background: #fff;
}

.robot-dot,
.target-dot,
.pickup-dot,
.route-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.robot-dot {
  background: #ff8a2a;
}

.target-dot {
  background: #49c8ff;
}

.pickup-dot {
  background: #8ff1c7;
}

.route-dot {
  background: #26a7ff;
}

.order-robot-marker {
  position: relative;
  width: 46px;
  height: 34px;
  transform: rotate(var(--vehicle-heading)) rotate(90deg);
  transform-origin: 50% 50%;
  filter: drop-shadow(0 12px 16px rgba(0, 0, 0, 0.28));
}

.order-robot-marker::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 40px;
  height: 29px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(255, 138, 42, 0.14);
  animation: orderVehiclePulse 1.8s ease-out infinite;
}

.vehicle-shadow {
  position: absolute;
  left: 7px;
  right: 6px;
  bottom: 2px;
  height: 9px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.22);
  filter: blur(5px);
}

.vehicle-body {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 34px;
  height: 23px;
  transform: translate(-50%, -50%);
  border-radius: 14px 15px 12px 13px;
  background:
    linear-gradient(110deg, rgba(255, 255, 255, 0.46), transparent 28%),
    linear-gradient(180deg, #d3d0c9 0%, #8f8b84 48%, #4f4d4a 100%);
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow:
    inset 0 5px 8px rgba(255, 255, 255, 0.55),
    inset 0 -8px 12px rgba(0, 0, 0, 0.24);
}

.vehicle-roof {
  position: absolute;
  left: 10px;
  top: -3px;
  width: 14px;
  height: 5px;
  border-radius: 8px 8px 3px 3px;
  background: linear-gradient(180deg, #f2f0ea, #6e6b67);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
}

.vehicle-glass {
  position: absolute;
  left: 10px;
  top: 3px;
  width: 17px;
  height: 16px;
  border-radius: 7px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.26), transparent 36%),
    linear-gradient(180deg, #151719, #030405);
  border: 2px solid rgba(220, 216, 205, 0.78);
  box-shadow: inset 0 0 12px rgba(111, 188, 255, 0.13);
}

.vehicle-door-line {
  position: absolute;
  left: 18px;
  top: 5px;
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.16);
}

.wheel {
  position: absolute;
  bottom: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background:
    radial-gradient(circle, #f3efe8 0 18%, #aaa49a 20% 36%, #f7f5ef 38% 55%, #6d6862 57% 70%, #151719 72%);
  border: 2px solid rgba(255, 255, 255, 0.72);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.34);
}

.wheel-left {
  left: 4px;
}

.wheel-right {
  right: 4px;
}

.tail-light {
  position: absolute;
  left: 2px;
  top: 11px;
  width: 8px;
  height: 3px;
  border-radius: 999px;
  background: #ff3d34;
  box-shadow: 0 0 8px rgba(255, 61, 52, 0.78);
}

.front-light {
  position: absolute;
  right: 2px;
  top: 11px;
  width: 7px;
  height: 3px;
  border-radius: 999px;
  background: #eef7ff;
  box-shadow: 0 0 8px rgba(142, 215, 255, 0.82);
}

@keyframes orderVehiclePulse {
  0% {
    opacity: 0.9;
    transform: scale(0.72);
  }
  100% {
    opacity: 0;
    transform: scale(1.28);
  }
}

@media (max-width: 1100px) {
  .order-map-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .order-map-head,
  .eta-grid {
    grid-template-columns: 1fr;
  }

  .order-map-panel,
  .wgs-map-canvas {
    min-height: 300px;
  }
}
</style>
